import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog } from '@/shared/ui/Modal';
import { useLocation } from '@/shared/hooks/useLocation';
import { lifecycleApi } from '../api/lifecycleApi';
import { strategyApi } from '../api/strategyApi';
import { strategyKeys } from '../queryKeys';
import { WIZARD_STEPS } from '../components/StepNavBar';
import { EmptyState, Skeleton } from '@/shared/ui';
import styles from './StrategyOverviewPage.module.css';
import { NotStartedView } from './NotStartedView';
import { InProgressView } from './InProgressView';
import { ActiveView } from './ActiveView';
import { useToast } from '@/shared/ui/Toast';

export function StrategyOverviewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();
  const autoOpenModal = searchParams.get('vapiModal') === 'now' && !!locationId;
  const {
    data: lifecycleRes,
    isLoading,
    isError: lifecycleError,
    refetch: refetchLifecycle,
  } = useQuery({
    queryKey: strategyKeys.lifecycle(locationId),
    queryFn: () => lifecycleApi.getLifecycle(locationId),
    enabled: !!locationId,
  });

  const { data: insightsRes } = useQuery({
    queryKey: strategyKeys.insights(locationId),
    queryFn: () => strategyApi.getInsights(locationId),
    enabled: !!locationId,
  });

  const { data: overviewRes } = useQuery({
    queryKey: strategyKeys.overview(locationId),
    queryFn: () => strategyApi.getOverview(locationId),
    enabled: !!locationId,
  });

  const { showToast } = useToast();
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const { mutate: start, isPending: starting } = useMutation({
    mutationFn: () => lifecycleApi.startStrategy(locationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: strategyKeys.lifecycle(locationId) });
      navigate('/strategy/builder/profile');
    },
    onError: (err: unknown) => {
      showToast((err as Error).message || 'Could not start the strategy', 'error');
    },
  });

  // "Edit Strategy" works on a refresh draft, never on the live version's
  // inputs: those have no undo, and campaigns run on them.
  const { mutate: openRefresh, isPending: openingRefresh } = useMutation({
    mutationFn: () => lifecycleApi.openRefresh(locationId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: strategyKeys.lifecycle(locationId) });
      navigate(`/strategy/builder/profile?version=${res.data.version}`);
    },
    onError: (err: unknown) => {
      showToast((err as Error).message || 'Could not start a strategy refresh', 'error');
    },
  });

  const { mutate: discardDraftMut, isPending: discardingDraft } = useMutation({
    mutationFn: (version: number | undefined) => lifecycleApi.discardDraft(locationId, version),
    onSuccess: () => {
      // Discard puts answers, radius, competitor picks and uploads back, so
      // every strategy query for this location is out of date.
      queryClient.invalidateQueries({
        predicate: (q) => String(q.queryKey[0]).startsWith('strategy'),
      });
      showToast('Draft discarded — your inputs are back to the live strategy', 'success');
    },
    onError: (err: unknown) => {
      showToast((err as Error).message ?? 'Failed to discard draft', 'error');
    },
  });

  if (!locationId) {
    return (
      <EmptyState
        title="No location selected"
        description="Pick a location to see or build its strategy."
      />
    );
  }

  if (isLoading) {
    return (
      <div className={styles.loadingWrapper}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={80} borderRadius="10px" className={styles.skeletonItem} />
        ))}
      </div>
    );
  }

  const lifecycle = lifecycleRes?.data;
  const insights = insightsRes?.data ?? null;
  const overview = overviewRes?.data ?? null;

  if (!lifecycle) {
    return (
      <EmptyState
        title={lifecycleError ? "Couldn't load your strategy" : 'Strategy unavailable'}
        description="Something went wrong loading this location's strategy. Try again in a moment."
        action={{ label: 'Retry', onClick: () => void refetchLifecycle() }}
      />
    );
  }

  const stepToPath = (s?: number | null) =>
    WIZARD_STEPS.find((w) => w.id === (s ?? 1))?.path ?? 'profile';

  if (lifecycle.active) {
    const draftRow =
      lifecycle.strategy && lifecycle.strategy.id !== lifecycle.active.id
        ? lifecycle.strategy
        : null;
    return (
      <>
        <ActiveView
          lifecycle={lifecycle}
          insights={insights}
          overview={overview}
          draft={draftRow}
          onResumeDraft={
            draftRow
              ? () =>
                  navigate(
                    `/strategy/builder/${stepToPath(draftRow.currentStep)}?version=${draftRow.version}`,
                  )
              : undefined
          }
          onDiscardDraft={draftRow ? () => setDiscardConfirmOpen(true) : undefined}
          discardingDraft={discardingDraft}
          onEdit={() => {
            // Edit the open draft, or open one: editing in place would change
            // the live strategy's inputs with no way back.
            if (draftRow) navigate(`/strategy/builder/profile?version=${draftRow.version}`);
            else if (!openingRefresh) openRefresh();
          }}
          editing={openingRefresh}
          onNavigateStep={(step) =>
            navigate(`/strategy/builder/${stepToPath(step)}?version=${lifecycle.active?.version}`)
          }
        />
        <ConfirmDialog
          isOpen={discardConfirmOpen}
          onClose={() => setDiscardConfirmOpen(false)}
          onConfirm={() => {
            setDiscardConfirmOpen(false);
            discardDraftMut(draftRow?.version);
          }}
          title="Discard strategy refresh?"
          message={
            draftRow
              ? `Version ${draftRow.version} will be archived. Your questionnaire answers, market radius, competitor picks and uploaded reports go back to what version ${lifecycle.active.version} was built from. Practice details (address, services, providers…) are kept. Your active strategy is unchanged.`
              : 'This will archive the in-progress draft.'
          }
          confirmLabel="Discard draft"
          variant="destructive"
          isLoading={discardingDraft}
        />
      </>
    );
  }

  if (lifecycle.strategy?.status === 'in_progress') {
    return (
      <InProgressView
        lifecycle={lifecycle}
        onResume={() =>
          navigate(`/strategy/builder/${stepToPath(lifecycle.strategy?.currentStep)}`)
        }
        onNavigateStep={(s) => navigate(`/strategy/builder/${stepToPath(s)}`)}
        locationId={locationId}
        locationPhone={currentLocation?.phone}
        autoOpenModal={autoOpenModal}
      />
    );
  }

  return (
    <NotStartedView
      onStart={() => {
        if (!starting) start();
      }}
      starting={starting}
      locationId={locationId}
      locationPhone={currentLocation?.phone}
      autoOpenModal={autoOpenModal}
    />
  );
}
